import { useCallback, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import ErrorModal from "./ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import { useApi } from "../utils/Api";

const REVIEW_DEFAULTS = {
  name: "",
  rating: 0,
  review: "",
};

const renderStars = (rating = 0) => {
  const safeRating = Math.max(0, Math.min(5, Number(rating || 0)));
  return "★".repeat(safeRating) + "☆".repeat(5 - safeRating);
};

const StarRating = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= (hover || value);

        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="text-2xl leading-none transition-transform hover:scale-110"
            aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
          >
            <span className={active ? "text-pink-500" : "text-black/20"}>
              ★
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default function ReviewsSection() {
  const { apiFetch } = useApi();
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [reviewsError, setReviewsError] = useState("");

  const [reviewForm, setReviewForm] = useState(REVIEW_DEFAULTS);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState("");

  const loadReviews = useCallback(async () => {
    try {
      setLoadingReviews(true);
      setReviewsError("");

      const res = await apiFetch("get_reviews");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load reviews.");
      }

      const mappedReviews = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name || "Anonymous",
          rating: Number(item.rating || 5),
          review: item.review || "",
          createdAt: item.created_at || "",
        })
      );

      setReviews(mappedReviews);
    } catch (err) {
      setReviews([]);
      setReviewsError(err.message || "Unable to load reviews.");
    } finally {
      setLoadingReviews(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const onReviewChange = (key) => (e) => {
    const value = e.target.value;

    setReviewForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (reviewSuccess) setReviewSuccess("");
    if (isErrorOpen) closeError();
  };

  const handleRatingChange = (rating) => {
    setReviewForm((prev) => ({ ...prev, rating }));
    if (reviewSuccess) setReviewSuccess("");
    if (isErrorOpen) closeError();
  };

  const validateReviewForm = () => {
    if (!reviewForm.name.trim()) return "Please enter your name.";
    if (!reviewForm.review.trim()) return "Please enter your review.";
    if (!reviewForm.rating) return "Please select a rating.";
    return "";
  };

  const submitReview = async (e) => {
    e.preventDefault();

    const error = validateReviewForm();
    if (error) {
      showError(error);
      return;
    }

    try {
      setSubmittingReview(true);
      setReviewSuccess("");
      closeError();

      const payload = {
        name: reviewForm.name.trim(),
        rating: Number(reviewForm.rating),
        review: reviewForm.review.trim(),
      };

      const res = await apiFetch("submit_review", payload);

      if (!res?.success) {
        throw new Error(res?.message || "Unable to submit review.");
      }

      setReviewForm(REVIEW_DEFAULTS);
      setReviewSuccess("Thank you. Your review has been submitted.");
      loadReviews();
    } catch (err) {
      showError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <>
      <section className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
          <div className="text-center">
            <p className="text-sm uppercase tracking-[0.35em] text-pink-500/80">
              Client Reviews
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-black md:text-4xl">
              What our clients are saying.
            </h2>
          </div>

          {loadingReviews ? (
            <div className="mt-12 rounded-2xl border border-black/10 bg-neutral-50 p-8 text-center text-black/60">
              Loading reviews...
            </div>
          ) : reviewsError ? (
            <div className="mt-12 rounded-2xl border border-red-500/20 bg-red-50 p-8 text-center text-red-600">
              {reviewsError}
            </div>
          ) : reviews.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-black/10 bg-neutral-50 p-8 text-center text-black/60">
              No reviews yet.
            </div>
          ) : reviews.length === 1 ? (
            <div className="mt-12 flex justify-center">
              <div className="flex min-h-[230px] w-full max-w-sm flex-col rounded-2xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
                <div className="text-lg tracking-wider text-pink-500">
                  {renderStars(reviews[0].rating)}
                </div>
                <p className="mt-4 flex-grow text-sm leading-7 text-black/75">
                  "{reviews[0].review}"
                </p>
                <div className="mt-5 border-t border-black/10 pt-4">
                  <p className="text-sm font-semibold text-black">
                    {reviews[0].name}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative mt-12 md:px-14">
              {/* Prev arrow */}
              <button
                ref={prevRef}
                aria-label="Previous reviews"
                className="absolute left-0 top-[calc(50%-20px)] z-10 -translate-y-1/2 hidden h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/50 shadow-sm transition hover:border-pink-400 hover:text-pink-500 disabled:opacity-30 md:flex"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              {/* Next arrow */}
              <button
                ref={nextRef}
                aria-label="Next reviews"
                className="absolute right-0 top-[calc(50%-20px)] z-10 -translate-y-1/2 hidden h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-black/50 shadow-sm transition hover:border-pink-400 hover:text-pink-500 disabled:opacity-30 md:flex"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>

              <Swiper
                modules={[Navigation, Pagination, A11y]}
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }}
                onSwiper={(swiper) => {
                  // Re-assign after mount so refs are populated
                  swiper.params.navigation.prevEl = prevRef.current;
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.destroy();
                  swiper.navigation.init();
                  swiper.navigation.update();
                }}
                pagination={{ clickable: true }}
                spaceBetween={24}
                slidesPerView={1}
                breakpoints={{
                  768: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                className="reviews-swiper !pb-10"
              >
                {reviews.map((item) => (
                  <SwiperSlide key={item.id} className="flex h-auto">
                    <div className="flex min-h-[230px] w-full flex-col rounded-2xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
                      <div className="text-lg tracking-wider text-pink-500">
                        {renderStars(item.rating)}
                      </div>
                      <p className="mt-4 flex-grow text-sm leading-7 text-black/75">
                        "{item.review}"
                      </p>
                      <div className="mt-5 border-t border-black/10 pt-4">
                        <p className="text-sm font-semibold text-black">
                          {item.name}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* Review form */}
          <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-black/10 bg-[#fff7fb] p-6 md:p-8">
            <div className="text-center">
              <p className="text-sm uppercase tracking-[0.3em] text-pink-500/80">
                Leave a Review
              </p>
              <h3 className="mt-2 text-2xl font-semibold text-black">
                Share your experience with us
              </h3>
            </div>

            <form onSubmit={submitReview} className="mt-8 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">
                  Your name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reviewForm.name}
                  onChange={onReviewChange("name")}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-black outline-none transition focus:border-pink-500"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">
                  Rating <span className="text-red-500">*</span>
                </label>
                <StarRating
                  value={reviewForm.rating}
                  onChange={handleRatingChange}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">
                  Review <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={5}
                  value={reviewForm.review}
                  onChange={onReviewChange("review")}
                  className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-black outline-none transition focus:border-pink-500"
                  placeholder="Tell us about your experience"
                />
              </div>

              {reviewSuccess && (
                <div className="rounded-2xl border border-green-500/20 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {reviewSuccess}
                </div>
              )}

              <button
                type="submit"
                disabled={submittingReview}
                className={[
                  "w-full rounded-full px-6 py-3 text-sm font-semibold text-white transition",
                  submittingReview
                    ? "cursor-not-allowed bg-pink-300"
                    : "bg-pink-500 hover:bg-pink-600",
                ].join(" ")}
              >
                {submittingReview ? "Submitting..." : "Post Review"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}